// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CustomDatePicker from "src/components/shared/inputs/CustomDatePicker/CustomDatePicker";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Utils
import { getLocale } from "src/utils/locale/localeHelper";

// Constants
import { priorityLanguageMap } from "src/config/languages";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  ModalCustomConfirmGrey,
  ModalCustomDelete,
} from "src/styles/mui/muiStylesHelper";

// Icons
import { TrashIcon } from "src/assets/icons";

// Types
import {
  AddPatientInterventionType,
  PatientInterventionType,
} from "src/services/patients/types";

type AddEditInterventionModalType = {
  interventionTypes: Array<{
    id: number;
    type: string;
    label: string;
    color: string;
    icon: JSX.Element;
  }>;
  isOpen: boolean;
  onClose: () => void;
  typeToEdit?: PatientInterventionType["interventions"][0];
  onSave: (input: AddPatientInterventionType) => void;
  onDelete: (id: string) => void;
};

const AddEditInterventionModal = ({
  interventionTypes,
  isOpen,
  onClose,
  typeToEdit,
  onSave,
  onDelete,
}: AddEditInterventionModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { clinicInfo } = useClinicContext();

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;
  const timezone = clinicInfo?.timezone ?? DateTime.local().zoneName;

  const [inputFields, setInputFields] = useState<AddPatientInterventionType>({
    notes: "",
    interventionDate: "",
    interventionType: "Other",
  });
  const [scheduledError, setScheduledError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setScheduledError(false);

    if (typeToEdit) {
      setInputFields({
        notes: typeToEdit.notes,
        interventionDate: typeToEdit.interventionDate,
        interventionType: typeToEdit.interventionType,
      });
    } else {
      const todayISO =
        DateTime.now().setZone(timezone).startOf("day").toUTC().toISO() ?? "";
      setInputFields({
        notes: "",
        interventionDate: todayISO,
        interventionType: "Other",
      });
    }
  }, [isOpen, typeToEdit, timezone]);

  const scheduledDate = inputFields.interventionDate
    ? DateTime.fromISO(inputFields.interventionDate, { zone: "utc" }).setZone(
        timezone
      )
    : DateTime.now().setZone(timezone).startOf("day");

  const handleChangeDate = useCallback(
    (newValue: DateTime | null) => {
      const newValueInZone = newValue?.setZone(timezone)?.startOf("day");
      const today = DateTime.now().setZone(timezone).startOf("day");

      if (newValueInZone && newValueInZone.isValid && newValueInZone <= today) {
        setScheduledError(false);
        setInputFields((prev) => ({
          ...prev,
          interventionDate: newValueInZone.toUTC().toISO() ?? "",
        }));
      } else {
        setScheduledError(true);
      }
    },
    [timezone]
  );

  const handleSave = useCallback(() => {
    onClose();
    onSave(inputFields);
  }, [onClose, onSave, inputFields]);

  const handleDelete = useCallback(() => {
    if (typeToEdit) {
      onClose();
      onDelete(typeToEdit.id);
    }
  }, [onClose, onDelete, typeToEdit]);

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      title={typeToEdit ? t("Edit Timeline Event") : t("Add Timeline Event")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          {typeToEdit ? (
            <Button
              sx={ModalCustomDelete}
              startIcon={<TrashIcon />}
              onClick={handleDelete}
            >
              {t("Delete")}
            </Button>
          ) : (
            <Box />
          )}
          <Grid
            sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <Button sx={ModalCustomCancel} onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button sx={ModalCustomConfirm} onClick={handleSave}>
              {t("Save")}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="8px">
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: "14px" }}>{t("Type")}</Typography>
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {interventionTypes.map((interventionType) => (
              <Button
                key={`${interventionType.label}`}
                startIcon={interventionType.icon}
                sx={{
                  ...(inputFields.interventionType === interventionType.type
                    ? {
                        ...ModalCustomConfirmGrey(),
                        backgroundColor: "#E2772E",
                        border: "1px solid #E2772E",
                        "&:hover": {
                          backgroundColor: "#E2772E",
                          border: "1px solid #E2772E",
                        },
                      }
                    : ModalCustomCancel(theme)),
                  width: "fit-content",
                  height: "fit-content",
                  fontSize: "14px",
                }}
                onClick={() =>
                  setInputFields((prev) => ({
                    ...prev,
                    interventionType: interventionType.type,
                  }))
                }
              >
                <Typography sx={{ fontSize: "14px" }}>
                  {interventionType.label}
                </Typography>
              </Button>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: "14px" }}>{t("Date")}</Typography>
            <LocalizationProvider
              dateAdapter={AdapterLuxon}
              adapterLocale={getLocale(currentLang).locale}
            >
              <CustomDatePicker
                disableFuture
                openTo="year"
                date={scheduledDate}
                handleChange={handleChangeDate}
                hasError={scheduledError}
                minDay={DateTime.now()
                  .minus({ years: 20 })
                  .startOf("year")
                  .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
                  .startOf("day")}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: "14px" }}>{t("Notes")}</Typography>
            <TextInputField
              id="intervention-notes-input"
              required
              value={inputFields.notes}
              onChange={(event) =>
                setInputFields((prev) => ({
                  ...prev,
                  notes: event.target.value,
                }))
              }
              placeholder={t("Enter a value")}
            />
          </Box>
        </Box>
      </Grid>
    </DefaultModal>
  );
};

export default AddEditInterventionModal;
